import { authenticated_api } from './base';
import {
  isAnswerEnrichmentAction,
  isStrategicEnrichmentAction,
} from '../helper/chatArtifactActionKinds';
import { isMockChatSessionsEnabled, createMockChatSession, getMockChatSession, listMockChatSessions, sendMockChatMessage } from '../fixture/mockChatSessions';

const handleChatApiError = (error) => {
  if (process.env.NODE_ENV !== 'production') console.log(error);
  return error.response;
};

// FE #746: an error with no `response` means the browser dropped the request, so the
// server never answered. Report that explicitly; callers cannot tell `undefined`
// apart from a refusal, and a lost send may still have landed on the server.
const handleChatRequestError = (error) =>
  handleChatApiError(error) || { networkError: true, data: null };

export const createChatSession = async (workspaceId = null) => {
  if (isMockChatSessionsEnabled()) {
    return createMockChatSession();
  }
  try {
    return await authenticated_api.post('/chat/sessions', {
      workspace_id: workspaceId,
    });
  } catch (error) {
    return handleChatRequestError(error);
  }
};

export const sendChatMessage = async (sessionId, content, idempotencyKey = null) => {
  if (isMockChatSessionsEnabled()) {
    return sendMockChatMessage(sessionId, content);
  }
  try {
    return await authenticated_api.post(`/chat/sessions/${sessionId}/messages`, {
      content,
      ...(idempotencyKey ? { idempotency_key: idempotencyKey } : {}),
    });
  } catch (error) {
    return handleChatRequestError(error);
  }
};

export const getChatSession = async (sessionId) => {
  if (isMockChatSessionsEnabled()) {
    return getMockChatSession(sessionId);
  }
  try {
    return await authenticated_api.get(`/chat/sessions/${sessionId}`);
  } catch (error) {
    return handleChatRequestError(error);
  }
};

export const listChatSessions = async (limit = 30) => {
  if (isMockChatSessionsEnabled()) {
    return listMockChatSessions(limit);
  }
  try {
    return await authenticated_api.get('/chat/sessions', { params: { limit } });
  } catch (error) {
    return handleChatRequestError(error);
  }
};

export const getChatJob = async (jobId) => {
  try {
    return await authenticated_api.get(`/chat/jobs/${jobId}`);
  } catch (error) {
    return handleChatApiError(error);
  }
};

export const getChatArtifact = async (artifactId, options = {}) => {
  try {
    const query = options.jobId ? `?job_id=${encodeURIComponent(options.jobId)}` : "";
    return await authenticated_api.get(`/chat/artifacts/${artifactId}${query}`);
  } catch (error) {
    return handleChatApiError(error);
  }
};

export const submitChatArtifactSuggestion = async (artifactId, suggestion, idempotencyKey) => {
  const suggestionKey = typeof suggestion === 'string'
    ? suggestion
    : suggestion?.suggestion_key || suggestion?.key;
  const method = String(suggestion?.endpoint?.method || 'POST').toUpperCase();
  const path = suggestion?.endpoint?.path || (
    artifactId && suggestionKey
      ? `/chat/artifacts/${artifactId}/suggestions/${suggestionKey}/submit`
      : null
  );
  if (!path || method !== 'POST') {
    return {
      status: 422,
      data: { message: 'This artifact suggestion is not available.' },
    };
  }
  try {
    return await authenticated_api.post(
      path,
      { idempotency_key: idempotencyKey }
    );
  } catch (error) {
    return handleChatApiError(error);
  }
};

export const exportChatCompanies = async (companyIds, options = {}) => {
  const payload = options.artifactId
    ? { artifact_id: options.artifactId }
    : { company_ids: companyIds };
  const path = options.action?.endpoint?.path || '/export/companies';
  try {
    return await authenticated_api.post(
      path,
      payload,
      { responseType: 'blob', timeout: 60000 }
    );
  } catch (error) {
    return handleChatApiError(error);
  }
};

function buildArtifactActionBody(action, payload = {}) {
  if (isAnswerEnrichmentAction(action)) {
    const path = String(action?.endpoint?.path || '');
    const replayAction = path.includes('/retry-failed-rows') || path.includes('/refresh-all');
    const query = payload.query || action?.query;
    const outputIdentity = payload.outputIdentity || payload.output_identity || action?.output_identity;
    const idempotencyKey = payload.idempotencyKey || payload.idempotency_key;
    return {
      ...(replayAction && outputIdentity ? { output_identity: outputIdentity } : {}),
      ...(!replayAction && query ? { query } : {}),
      ...(idempotencyKey ? { idempotency_key: idempotencyKey } : {}),
    };
  }

  if (isStrategicEnrichmentAction(action)) {
    const idempotencyKey = payload.idempotencyKey || payload.idempotency_key;
    return idempotencyKey ? { idempotency_key: idempotencyKey } : {};
  }

  if (isSampleScopeAction(action)) {
    return {};
  }

  const body = {};
  if (payload.companyIds?.length) body.company_ids = payload.companyIds;
  const idempotencyKey = payload.idempotencyKey || payload.idempotency_key;
  if (idempotencyKey) body.idempotency_key = idempotencyKey;
  if (action?.endpoint?.path?.endsWith("/enrich") && payload.actionLabel) {
    body.action_label = payload.actionLabel;
  }
  return body;
}

function isSampleScopeAction(action) {
  return action?.key === 'find_leads_sample' || action?.scope === 'sample_preview';
}

export const runChatArtifactAction = async (action, payload = {}) => {
  const method = String(action?.endpoint?.method || 'POST').toUpperCase();
  const path = action?.endpoint?.path;
  if (!path || method !== 'POST') {
    return {
      status: 422,
      data: { message: 'This artifact action is not available.' },
    };
  }

  const body = buildArtifactActionBody(action, payload);
  try {
    return await authenticated_api.post(path, body);
  } catch (error) {
    return handleChatApiError(error);
  }
};

export const getChatConnectors = async () => {
  try {
    return await authenticated_api.get('/chat/connectors');
  } catch (error) {
    return handleChatApiError(error);
  }
};

export const runChatArtifactConnector = async (artifactId, connectorKey, companyIds) => {
  try {
    return await authenticated_api.post(
      `/chat/artifacts/${artifactId}/connectors/${connectorKey}/run`,
      { company_ids: companyIds }
    );
  } catch (error) {
    return handleChatApiError(error);
  }
};

export const enrichChatArtifact = async (artifactId, companyIds) => {
  try {
    return await authenticated_api.post(`/chat/artifacts/${artifactId}/enrich`, {
      company_ids: companyIds,
    });
  } catch (error) {
    return handleChatApiError(error);
  }
};
